import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tour.css';

const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT_ESTIMATE = 260;
const GUTTER = 24;

/**
 * Resolve a step's target into a DOMRect (or null for centered modal steps).
 * Returns a fresh rect each call so layout shifts are picked up.
 */
function resolveTargetRect(target) {
  if (!target) return null;
  let el = null;
  if (typeof target === 'string') el = document.querySelector(target);
  else if (target instanceof Element) el = target;
  else if (target?.current instanceof Element) el = target.current;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Skip elements that are not rendered / have zero size
  if (r.width === 0 && r.height === 0) return null;
  return r;
}

/**
 * Pick the best tooltip placement given the target rect and viewport.
 * placement: 'top' | 'bottom' | 'left' | 'right' | 'auto'
 */
function computeTooltipPos(rect, placement = 'auto') {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return null; // caller should render centered tooltip

  const fits = (p) => {
    if (p === 'bottom') return rect.bottom + GUTTER + TOOLTIP_HEIGHT_ESTIMATE <= vh - 8;
    if (p === 'top')    return rect.top - GUTTER - TOOLTIP_HEIGHT_ESTIMATE >= 8;
    if (p === 'right')  return rect.right + GUTTER + TOOLTIP_WIDTH <= vw - 8;
    if (p === 'left')   return rect.left - GUTTER - TOOLTIP_WIDTH >= 8;
    return false;
  };
  // Honour explicit placement when it fits. If it doesn't fit, flip to
  // the opposite side first (e.g. 'left' fails → try 'right'), then the
  // auto-fallback list. This avoids the case where a forced placement
  // gets clamped INSIDE the target's bounds (tooltip covers the element).
  const OPPOSITE = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
  const chosen = (() => {
    if (placement === 'auto') return ['bottom', 'top', 'right', 'left'].find(fits) || 'bottom';
    if (fits(placement)) return placement;
    const opp = OPPOSITE[placement];
    if (opp && fits(opp)) return opp;
    return ['bottom', 'top', 'right', 'left'].find(fits) || placement;
  })();

  let top, left;
  if (chosen === 'bottom') {
    top = rect.bottom + GUTTER;
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  } else if (chosen === 'top') {
    top = rect.top - GUTTER - TOOLTIP_HEIGHT_ESTIMATE;
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  } else if (chosen === 'right') {
    top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2;
    left = rect.right + GUTTER;
  } else {
    top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2;
    left = rect.left - GUTTER - TOOLTIP_WIDTH;
  }
  // Clamp to viewport
  if (left < 8) left = 8;
  if (left + TOOLTIP_WIDTH > vw - 8) left = vw - TOOLTIP_WIDTH - 8;
  if (top < 8) top = 8;
  if (top + TOOLTIP_HEIGHT_ESTIMATE > vh - 8) top = vh - TOOLTIP_HEIGHT_ESTIMATE - 8;
  return { top, left, placement: chosen };
}

export default function Tour({ steps, isOpen, onClose, onComplete }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  const step = isOpen ? steps[index] : null;
  const isCentered = !step?.target;

  // Recompute target rect + tooltip position on step change, resize, scroll
  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (isCentered) {
        setRect(null);
        setTooltipPos(null);
        return;
      }
      const r = resolveTargetRect(step.target);
      setRect(r);
      setTooltipPos(r ? computeTooltipPos(r, step.placement) : null);
    };
    update();
    // Some targets mount slightly later (e.g. after a navigation). Re-query
    // for a few frames to catch them.
    const retries = [50, 150, 400];
    const timers = retries.map(ms => setTimeout(update, ms));
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index, isCentered, step?.target]);

  // Run setup() if present (e.g. open a popup the next step targets) and
  // scroll the target into view when a new step starts. Use block: 'nearest'
  // so we don't move the page when the target is already on screen — that
  // was making banner content slide up and stay there even after the tour
  // ended.
  useEffect(() => {
    if (!isOpen) return;
    if (typeof step?.setup === 'function') {
      try { step.setup(); } catch (e) { console.warn('[Tour] setup failed', e); }
    }
    if (!step?.target) return;
    const el = typeof step.target === 'string'
      ? document.querySelector(step.target)
      : (step.target instanceof Element ? step.target : step.target?.current);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isOpen, index, step]);

  // Remember the scroll position at the moment the tour opened and restore
  // it when it closes — even if our 'nearest' policy nudges the page, we
  // hand it back exactly where the teacher / student left it.
  const initialScrollRef = React.useRef(null);
  useEffect(() => {
    if (isOpen) {
      initialScrollRef.current = { x: window.scrollX, y: window.scrollY };
    } else if (initialScrollRef.current) {
      const { x, y } = initialScrollRef.current;
      window.scrollTo({ left: x, top: y, behavior: 'auto' });
      initialScrollRef.current = null;
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape')      handleSkip();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft')  handleBack();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index]);

  // Reset index when reopening
  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen]);

  const handleNext = () => {
    if (index < steps.length - 1) {
      setIndex(i => i + 1);
    } else {
      onComplete?.();
      onClose?.();
    }
  };
  const handleBack = () => {
    if (index > 0) setIndex(i => i - 1);
  };
  const handleSkip = () => onClose?.();

  if (!isOpen || !step) return null;

  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return createPortal(
    <>
      {/* Click blocker — captures clicks on the page during the tour */}
      <div className="tour-click-blocker" onClick={handleSkip} />

      {/* Spotlight cutout (or full dim for centered step) */}
      {rect && !isCentered ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        >
          <div className="tour-spotlight-ring" />
        </div>
      ) : (
        <div className="tour-spotlight is-centered" style={{ top: 0, left: 0, width: 0, height: 0 }} />
      )}

      {/* Tooltip */}
      <div
        className={`tour-tooltip${isCentered ? ' is-centered' : ''}`}
        style={isCentered ? undefined : tooltipPos ? { top: tooltipPos.top, left: tooltipPos.left } : undefined}
      >
        <div className="tour-stepper">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`tour-stepper-dot${i === index ? ' is-active' : ''}${i < index ? ' is-done' : ''}`}
            />
          ))}
        </div>
        <div className="tour-step-counter">Step {index + 1} of {total}</div>
        <h3 className="tour-tooltip-title">{step.title}</h3>
        <p className="tour-tooltip-body">{step.description}</p>
        {step.note && (
          <div className="tour-tooltip-note">
            <span className="tour-tooltip-note-icon" aria-hidden>⚠️</span>
            <span>{step.note}</span>
          </div>
        )}
        <div className="tour-tooltip-actions">
          <button type="button" className="tour-btn tour-btn-skip" onClick={handleSkip}>
            Skip tour
          </button>
          <div className="tour-spacer" />
          {!isFirst && (
            <button type="button" className="tour-btn tour-btn-back" onClick={handleBack}>
              ← Back
            </button>
          )}
          <button type="button" className="tour-btn tour-btn-next" onClick={handleNext}>
            {isLast ? (step.finishLabel || 'Finish') : 'Next →'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
